import type { ReportExport } from '@jaf/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useSession } from '@/features/auth';
import { notifyError } from '@/shared/ui/notify';
import {
  type IncomeStatementExportInput,
  requestIncomeStatementExport,
} from '../api/report-exports-api';
import { reportExportKeys } from '../api/report-exports-queries';
import { useDownloadReportExport } from './use-download-report-export';

/**
 * Pede a exportação da DRE. Pronta na hora: baixa o arquivo. Na fila: avisa que o relatório
 * está sendo gerado; o menu de exportações baixa sozinho quando fica pronto.
 */
export function useRequestIncomeStatementExport() {
  const organizationId = useSession((state) => state.session?.organizationId ?? '');
  const queryClient = useQueryClient();
  const download = useDownloadReportExport();
  return useMutation({
    mutationFn: (input: IncomeStatementExportInput) => requestIncomeStatementExport(input),
    onSuccess: async (reportExport: ReportExport) => {
      if (reportExport.status === 'READY') {
        await download(reportExport);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: reportExportKeys.all(organizationId) });
      if (reportExport.status === 'FAILED') {
        toast.error(reportExport.failureMessage ?? 'Não foi possível gerar o arquivo');
      } else {
        toast.info('Gerando o relatório', {
          description: 'É um relatório grande. O download começa sozinho quando estiver pronto.',
        });
      }
    },
    onError: (error) => {
      notifyError(error, 'Não foi possível exportar o relatório');
    },
  });
}
